/**
 * CRM Events `/live-events` item -> FE `OperationRow` mapping (the live Operations
 * feed's Operation / Category / Scope / Channel / Time / Status table).
 *
 * Kept out of the hook so the part that actually encodes backend knowledge -- the
 * status vocabulary and the id derivation -- is unit-testable without rendering.
 * Reads every field DEFENSIVELY (all optional) and always returns a valid row, so
 * a shape drift downgrades one cell rather than blanking the feed.
 *
 * Contract: `docs/api.owners-hub.operations.md`.
 */
import type { OperationRow, OperationStatus } from '../../../mock-data/types';
import type { LiveEventItem } from './crm-events';

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' && value ? value : undefined;

/**
 * Backend status -> the feed's caption, icon and tint, plus which side of "now"
 * the row belongs to.
 *
 * The first three are the vocabulary CRM Events actually emits. `not_sent` is a
 * blocked delivery (the observed `error_message` is "Blocked by comm flags"), so
 * it reads as a neutral outcome rather than an error the owner must chase. The
 * rest are the states the original design allowed for, kept so a pipeline that
 * starts emitting them renders properly instead of falling through.
 */
export const STATUS_BY_LABEL: Record<string, OperationStatus> = {
  scheduled: { label: 'Scheduled', tone: 'neutral', icon: 'clock', phase: 'scheduled' },
  sent: { label: 'Executed', tone: 'success', icon: 'check', phase: 'executed' },
  not_sent: { label: 'Not sent', tone: 'neutral', icon: 'close-circle', phase: 'executed' },
  queued: { label: 'Queued', tone: 'neutral', icon: 'clock', phase: 'scheduled' },
  executing: { label: 'Executing', tone: 'warning', icon: 'clock', phase: 'executed' },
  executed: { label: 'Executed', tone: 'success', icon: 'check', phase: 'executed' },
  failed: { label: 'Failed', tone: 'danger', icon: 'warning', phase: 'executed' },
};

/**
 * An unrecognised status keeps its raw label and renders neutral, so a new backend
 * state never breaks a row. Its phase is decided by the clock instead: a
 * future-dated event has not run yet, anything else already has.
 *
 * `Object.hasOwn` rather than a plain lookup so a status naming an
 * `Object.prototype` member (`__proto__`, `constructor`) misses the map and takes
 * the fallback, instead of resolving to a prototype member.
 */
export const toStatus = (raw: string | undefined, at: string | undefined, now: number): OperationStatus => {
  const key = (raw ?? '').toLowerCase();
  if (Object.hasOwn(STATUS_BY_LABEL, key)) return STATUS_BY_LABEL[key];
  const ms = at != null ? Date.parse(at) : Number.NaN;
  return { label: raw ?? 'Unknown', tone: 'neutral', icon: 'clock', phase: ms > now ? 'scheduled' : 'executed' };
};

/**
 * Item -> OperationRow. Reads the confirmed `title`/`timestamp` keys first, then
 * the alternates, with safe fallbacks.
 *
 * The row `id` must be STABLE across polls: the freshness diff and React keys both
 * hinge on it, so a value that shifts when the list re-orders would mis-flash rows
 * and remount them every refresh. We prefer the API's id, and when it gives none
 * fall back to a composite of the row's content + timestamp (NOT the array index,
 * which moves as rows arrive). `index` is only the terminal tiebreaker when there
 * is no id and no timestamp at all.
 *
 * `now` is the batch's clock, passed in so every row of one response is phased
 * against the same instant.
 */
export const toRow = (item: LiveEventItem, index: number, now: number): OperationRow => {
  const name = asString(item.title) ?? asString(item.operation) ?? asString(item.name) ?? 'Operation';
  const category = asString(item.category) ?? '';
  const scope = asString(item.scope) ?? '';
  const channel = asString(item.channel) ?? '';
  const at = asString(item.timestamp) ?? asString(item.at) ?? asString(item.created_at);
  const status = toStatus(asString(item.status), at, now);
  // The composite deliberately excludes the STATUS: it is the one field that
  // changes over a row's life (scheduled -> sent), and folding it in would mint a
  // new id the moment an operation ran, remounting the row and flashing it green
  // as though it had just arrived.
  const apiId = asString(item.id) ?? asString(item.event_id);
  const id = apiId ?? `op:${name}|${category}|${scope}|${channel}|${at ?? index}`;
  return { id, name, category, scope, channel, status, at };
};

/** Map one page of `/live-events` items, phasing them all against one clock. */
export const toRows = (items: LiveEventItem[], now: number): OperationRow[] =>
  items.map((item, index) => toRow(item, index, now));
